import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ServicePageLayout from '../../components/Layout/ServicePageLayout';
import { FaBrain, FaRobot, FaChartLine, FaCogs } from 'react-icons/fa';
import { SiTensorflow, SiPytorch, SiPython, SiOpenai } from 'react-icons/si';
import { MdEmail, MdPhone, MdPerson, MdMessage } from 'react-icons/md';
import { submitToGoogleSheet } from '../../utils/helpers';
import classes from './ServicePage.module.scss';
import contactClasses from '../../components/Contact/Contact.module.scss';
import FAQ from '../../components/FAQ/FAQ';
import aiMlHeroImg from '../../assets/ai_ml_service.png';
import artistNextDoorPortfolio from '../../assets/artistnextdoor_portfolio.png';
import palmistryPortfolio from '../../assets/palmistry_portfolio.png';
import machineLearningImg from '../../assets/machine_learning_service.png';
import naturalLanguageProcessingImg from '../../assets/natural_language_processing_service.png';
import computerVisionImg from '../../assets/computer_vision_service.png';
import predictiveAnalysisImg from '../../assets/predictive_analysis.png';

const AIMLDevelopment = () => {
  const { projects } = useSelector((state) => state.portfolio);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    message: '',
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToGoogleSheet(formData);
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      alert('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phoneNumber: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const aiProjects = [
    {
      id: 1,
      title: 'ARTIST NEXT DOOR',
      subtitle: 'AI-Powered Matching',
      description: 'Intelligent recommendation engine for matching artists with clients based on preferences, location, and past bookings.',
      image: artistNextDoorPortfolio,
      category: 'AI/ML',
      link: '/services/ai-ml-development',
      bgGradient: 'linear-gradient(135deg, rgb(133, 89, 200) 0%, rgb(52, 29, 89) 100%)'
    },
    {
      id: 2,
      title: 'PALMISTRY APP',
      subtitle: 'Computer Vision & ML',
      description: 'Palm reading application powered by computer vision and deep learning models for accurate line detection and analysis.',
      image: palmistryPortfolio,
      category: 'AI/ML',
      link: '/services/ai-ml-development',
      bgGradient: 'linear-gradient(135deg, rgb(45, 80, 120) 0%, rgb(20, 40, 70) 100%)'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % aiProjects.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + aiProjects.length) % aiProjects.length);
  };

  const technologies = [
    { name: 'TensorFlow', icon: <SiTensorflow />, description: 'Deep learning framework' },
    { name: 'PyTorch', icon: <SiPytorch />, description: 'ML research & production' },
    { name: 'Python', icon: <SiPython />, description: 'Core AI/ML language' },
    { name: 'OpenAI', icon: <SiOpenai />, description: 'LLM & generative AI' },
  ];

  const features = [
    {
      title: 'Machine Learning',
      description: 'Custom ML models trained on your data to automate decisions and uncover insights.',
      image: machineLearningImg
    },
    {
      title: 'Natural Language Processing',
      description: 'Build chatbots, sentiment analysis, and text understanding systems powered by LLMs.',
      image: naturalLanguageProcessingImg
    },
    {
      title: 'Computer Vision',
      description: 'Image recognition, object detection, and visual analysis for real-world applications.',
      image: computerVisionImg
    },
    {
      title: 'Predictive Analytics',
      description: 'Data-driven forecasting models that help you make smarter business decisions.',
      image: predictiveAnalysisImg
    }
  ];

  const faqs = [
    {
      question: 'What types of AI/ML solutions do you build?',
      answer: 'We build custom machine learning models, NLP chatbots, recommendation engines, computer vision systems, predictive analytics dashboards, and generative AI applications. Every solution is tailored to your specific business needs and data.'
    },
    {
      question: 'Do I need a large dataset to get started?',
      answer: 'Not necessarily. We can work with small datasets using transfer learning, data augmentation, and pre-trained models. For some use cases, we leverage foundation models like GPT-4 or open-source LLMs that require minimal training data.'
    },
    {
      question: 'How long does it take to develop an AI solution?',
      answer: 'A proof of concept typically takes 4-6 weeks. A production-ready AI solution takes 2-4 months, including data preparation, model training, testing, and deployment. Complex enterprise solutions may take longer depending on scope.'
    },
    {
      question: 'Can you integrate AI into my existing application?',
      answer: 'Yes. We build AI features as modular APIs that integrate seamlessly with your existing tech stack. Whether it is adding a chatbot, recommendation engine, or image recognition — we deploy via REST APIs or SDKs.'
    },
    {
      question: 'How do you ensure model accuracy and reliability?',
      answer: 'We follow rigorous ML practices — cross-validation, A/B testing, continuous monitoring, and automated retraining pipelines. We also implement fallback mechanisms and human-in-the-loop systems for critical decisions.'
    },
    {
      question: 'What about data privacy and security?',
      answer: 'Data security is paramount. We implement encryption at rest and in transit, follow GDPR compliance, use anonymization techniques, and can deploy models on-premise or in your private cloud for sensitive data.'
    },
  ];

  const process = [
    { step: '01', title: 'Data Assessment', description: 'Evaluating data quality, availability, and requirements' },
    { step: '02', title: 'Model Design', description: 'Selecting algorithms and designing the ML pipeline' },
    { step: '03', title: 'Data Engineering', description: 'Cleaning, transforming, and preparing training datasets' },
    { step: '04', title: 'Model Training', description: 'Training and fine-tuning models for optimal performance' },
    { step: '05', title: 'Validation & Testing', description: 'Rigorous testing with real-world scenarios and edge cases' },
    { step: '06', title: 'Deployment & Monitoring', description: 'Production deployment with continuous monitoring' },
  ];

  return (
    <ServicePageLayout>
      <div className={classes.servicePageContainer}>
        {/* Hero Section */}
        <section className={classes.hero}>
          <div className={classes.container}>
            <div className={classes.heroContent}>
              <div className={classes.heroHighlight}>
                <div className={classes.heroText}>
                  <h1 className={classes.heroTitle}>
                    AI & Machine Learning <span className={classes.gradient}>Development</span>
                  </h1>
                  <p className={classes.heroSubtitle}>
                    Harness the power of artificial intelligence and machine learning to automate processes,
                    unlock insights, and build intelligent applications that transform your business.
                  </p>
                  <div className={classes.heroCTA}>
                    <button
                      className={classes.exploreButton}
                      onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Explore Services
                    </button>
                    <button
                      className={classes.quoteButton}
                      onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get A Quote
                    </button>
                  </div>
                  <div className={classes.heroStats}>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>{aiProjects.length}+</span>
                      <span className={classes.statLabel}>AI Projects</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>95%</span>
                      <span className={classes.statLabel}>Model Accuracy</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>3x</span>
                      <span className={classes.statLabel}>Efficiency Boost</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.heroVisual}>
                <div className={classes.phoneContainer}>
                  <img
                    src={aiMlHeroImg}
                    alt="AI/ML Development"
                    className={classes.phoneImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className={classes.features}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Why Choose Our <span className={classes.gradient}>AI Solutions</span>
              </h2>
            </div>
            <div className={classes.featuresGrid}>
              {features.map((feature, index) => (
                <div key={index} className={classes.featureCard}>
                  <div className={classes.featureImageContainer}>
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={classes.featureImage}
                    />
                  </div>
                  <h3 className={classes.featureTitle}>{feature.title}</h3>
                  <p className={classes.featureDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className={classes.technologies}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                AI/ML <span className={classes.gradient}>Technologies</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Industry-leading frameworks and tools powering our intelligent solutions
              </p>
            </div>
            <div className={classes.techGrid}>
              {technologies.map((tech, index) => (
                <div key={index} className={classes.techCard}>
                  <div className={classes.techIcon}>{tech.icon}</div>
                  <h3 className={classes.techName}>{tech.name}</h3>
                  <p className={classes.techDescription}>{tech.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section - Carousel */}
        <section className={classes.portfolioSection}>
          <div className={classes.portfolioContainer}>
            <div className={classes.portfolioHeader}>
              <h2 className={classes.portfolioTitle}>
                Our AI <span className={classes.gradient}>Projects</span>
              </h2>
            </div>

            <div className={classes.carousel}>
              <div className={classes.slideContainer}>
                <div
                  className={classes.slides}
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {aiProjects.map((project, index) => (
                    <div
                      key={project.id}
                      className={classes.slide}
                      style={{ background: project.bgGradient }}
                    >
                      <div className={classes.slideContent}>
                        <div className={classes.slideTop}>
                          <div className={classes.imageContainer}>
                            <img src={project.image} alt={project.title} />
                          </div>
                        </div>

                        <div className={classes.slideBottom}>
                          <div className={classes.slideHeader}>
                            <h3 className={classes.projectTitle}>{project.title}</h3>
                            <span className={classes.categoryBadge}>{project.category}</span>
                          </div>

                          <h4 className={classes.projectSubtitle}>{project.subtitle}</h4>

                          <p className={classes.projectDescription}>{project.description}</p>

                          <a href={project.link} className={classes.learnMoreBtn}>
                            <span>Learn More</span>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={classes.navigation}>
                <button
                  className={`${classes.navBtn} ${classes.prevBtn}`}
                  onClick={prevSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <button
                  className={`${classes.navBtn} ${classes.nextBtn}`}
                  onClick={nextSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              <div className={classes.indicators}>
                {aiProjects.map((_, index) => (
                  <button
                    key={index}
                    className={`${classes.indicator} ${index === currentSlide ? classes.active : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className={classes.process}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our AI Development <span className={classes.gradient}>Process</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                A data-driven approach to building reliable AI solutions
              </p>
            </div>
            <div className={classes.processGrid}>
              {process.map((step, index) => (
                <div key={index} className={classes.processStep}>
                  <div className={classes.stepNumber}>{step.step}</div>
                  <h3 className={classes.stepTitle}>{step.title}</h3>
                  <p className={classes.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQ faqs={faqs} title="Frequently Asked" highlight="Questions" />

        {/* Contact Form Section */}
        <section id="contact-form" className={contactClasses.contact}>
          <div className={contactClasses.container}>
            <div className={contactClasses.header}>
              <span className={contactClasses.badge}>Get Your Quote</span>
              <h2 className={contactClasses.title}>
                Let's Build Your <span className={contactClasses.gradient}>AI Solution</span>
              </h2>
            </div>

            <div className={contactClasses.content}>
              <form className={contactClasses.form} onSubmit={handleSubmit}>
                <div className={contactClasses.formRow}>
                  <div className={contactClasses.formGroup}>
                    <label htmlFor="name">
                      <MdPerson className={contactClasses.labelIcon} />
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className={contactClasses.premiumInput}
                    />
                  </div>

                  <div className={contactClasses.formGroup}>
                    <label htmlFor="email">
                      <MdEmail className={contactClasses.labelIcon} />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@company.com"
                      className={contactClasses.premiumInput}
                    />
                  </div>
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="phoneNumber">
                    <MdPhone className={contactClasses.labelIcon} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    placeholder="+1 (555) 123-4567"
                    className={contactClasses.premiumInput}
                  />
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="message">
                    <MdMessage className={contactClasses.labelIcon} />
                    AI/ML Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your AI/ML project - data availability, use case, expected outcomes, timeline, and any specific requirements..."
                    className={contactClasses.premiumTextarea}
                  ></textarea>
                </div>

                <button type="submit" className={contactClasses.submitBtn} disabled={loading}>
                  <span className={contactClasses.btnText}>
                    {loading && <span className={contactClasses.spinner} />}
                    {loading ? 'Sending...' : 'Get My Quote'}
                  </span>
                  <div className={contactClasses.btnGlow}></div>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={classes.cta}>
          <div className={classes.container}>
            <div className={classes.ctaContent}>
              <h2 className={classes.ctaTitle}>
                Ready to Build Intelligent <span className={classes.gradient}>AI Solutions</span>?
              </h2>
              <p className={classes.ctaSubtitle}>
                Let's leverage AI and machine learning to transform your business
              </p>
              <a href="#contact-form" className={classes.ctaButton}>Start Your AI Project</a>
            </div>
          </div>
        </section>
      </div>
    </ServicePageLayout>
  );
};

export default AIMLDevelopment;
